import { TSyncCache } from 'core-components/types'
import { IFormStore } from 'src/core/classes/helpers/store/Interfaces'
import { updateQueries } from 'src/core/types/helpers/updateQueries'
import { mergeStoreData } from 'src/core/types/innerStorageHelpers/merge'

export class InnerStorageOperationsService<T, R> {
  private dataStore: IFormStore<T> = {
    data: [],
    queries: {},
    groups: [],
  } as IFormStore<T>

  constructor(private keyField: string) {}

  public saveToStore = async (
    query: string,
    data: {
      items: T[]
      total: number
    },
    hierarchyField?: string,
    closeExpandedRoot?: (root: R | null) => void
  ) => {
    if (hierarchyField) {
      const hierarchyGroupsOrdered = this.splitDataByHierarchy(data, hierarchyField)
      if (data.items) {
        this.dataStore = mergeStoreData<T, R>({
          dataStoreProp: this.dataStore,
          keyField: this.keyField,
          query,
          data,
          groups: hierarchyGroupsOrdered,
          hierarchyField,
          closeExpandedRoot,
        })
        return
      }
    }
    if (data.items) {
      this.dataStore = mergeStoreData<T, R>({
        dataStoreProp: this.dataStore,
        keyField: this.keyField,
        query,
        data,
      })
    }
  }

  public readFromStore = async (
    query: string,
    hierarchyField?: string,
    expanded?: (R | number | null)[],
    skipHierarchyFilter: boolean = false
  ): Promise<{
    items?: T[]
    total: number
    unknownRoots?: (R | null)[]
    groups?: T[][]
  }> => {
    const res = this.dataStore.queries[query]
    const groups = res ? res.groups || [] : []
    if (hierarchyField) {
      const result = {
        groups,
        items: undefined as T[] | undefined,
        total: 0,
        unknownRoots: [] as (R | null)[] | undefined,
      }
      if (expanded && !skipHierarchyFilter) {
        this.composeKnownRootsItems(groups, expanded, hierarchyField, result)
      } else if (skipHierarchyFilter) {
        if (res) {
          result.items = res.items
        }
        result.unknownRoots = undefined
      }
      return result
    }
    return res
  }

  public findItemInStore = (id: R, keyField?: string) => {
    return this.dataStore.data.find((el) => el[keyField || this.keyField] === id)
  }

  public updateItem: TSyncCache<T> = ({ model, keyField, hierarchyField }) => {
    const keyProperty = keyField || this.keyField
    const dataReplaced = this.replaceItem(this.dataStore.data, model, keyProperty)
    this.dataStore.data = !dataReplaced.result
      ? [model, ...(this.dataStore.data || [])]
      : dataReplaced.data
    const queriesKeys = Object.keys(this.dataStore.queries)
    for (const query of queriesKeys) {
      const queries = this.dataStore.queries[query]
      const queriesReplaced = this.replaceItem(queries?.items, model, keyField)
      this.dataStore.queries[query].items = queriesReplaced.result
        ? queriesReplaced.data
        : [model, ...this.dataStore.queries[query].items]
    }
    updateQueries({
      queries: this.dataStore.queries,
      availableData: this.dataStore.data,
      hierarchyField,
    })
  }

  public deleteItem: TSyncCache<T> = ({ model, keyField }) => {
    const queriesKeys = Object.keys(this.dataStore.queries)
    const currentForm: T[] = this.dataStore.data || []
    const keyProperty = keyField || this.keyField
    const newForm = currentForm.filter((el) => el[keyProperty] !== model[keyProperty])
    this.dataStore.data = [...newForm]
    for (const query of queriesKeys) {
      const newItems = this.dataStore.queries[query].items.filter(
        (el) => el[keyProperty] !== model[keyProperty]
      )
      this.dataStore.queries[query].items = [...newItems]
      this.dataStore.queries[query].total = newItems.length
      this.dataStore.queries[query].groups = this.dataStore.queries[query].groups?.reduce(
        (acc: T[][], group: T[]) => {
          const newGroupItems = group.filter((el) => el[keyProperty] !== model[keyProperty])
          return [...acc, newGroupItems]
        },
        []
      )
    }
  }

  private replaceItem = (storage: T[], newItem: T, keyField?: string) => {
    if (!storage) {
      return {
        data: [],
        result: false,
      }
    }
    const keyProperty = keyField || this.keyField
    const id = newItem[keyProperty]
    let result = false

    const data = storage.map((el) => {
      if (el[keyProperty] === id) {
        result = true
        return newItem
      }
      return el
    })

    return {
      data,
      result,
    }
  }

  public isQueryExistsInDataStore = (query: string) => this.dataStore.queries[query]

  public groupsSort = (sortField: string, keyField: string) => {
    for (const i in this.dataStore.queries) {
      const gr = this.dataStore.queries[i].groups
      if (gr) {
        gr.map((group) =>
          group.sort((a, b) => {
            if (a[keyField] > 0 && b[keyField] < 0) {
              return -1
            }
            if (b[keyField] > 0 && a[keyField] < 0) {
              return 1
            }
            if (a[sortField] > b[sortField]) {
              return 1
            }
            if (a[sortField] < b[sortField]) {
              return -1
            }
            return 0
          })
        )
      }
    }
  }

  public removeBatchFn = (keyField: string, key: any[]) => {
    const currentForm: T[] = this.dataStore.data || []
    const newForm = currentForm.filter((el) => {
      if (key.includes(el[keyField])) {
        el['DELETED'] = true
      }
      return !key.includes(el[keyField])
    })
    this.dataStore.data = [...newForm]
    this.dataStore.queries = {}

    return {
      ...this.dataStore,
    }
  }

  public removeFn = (keyField: string, key: number | string) => {
    const currentForm: T[] = this.dataStore.data || []
    const newForm = currentForm.filter((el) => {
      if (el[keyField] === key) {
        el['DELETED'] = true
      }
      return el[keyField] !== key
    })
    this.dataStore.data = [...newForm]
    this.dataStore.queries = {}

    return {
      ...this.dataStore,
    }
  }

  public getDataFromStore = (): T[] => {
    return this.dataStore.data
  }

  public getDataStore = () => {
    return this.dataStore
  }

  public removeQueries = () => {
    this.dataStore.queries = {}
    this.dataStore.data = []
  }

  public searchInStore = (keyValue: string, keyField?: string) => {
    return this.dataStore.data.find(
      (el) => (el[keyField || this.keyField] || '').toString() === (keyValue || '').toString()
    )
  }

  private splitDataByHierarchy = (data: { items: T[]; total: number }, hierarchyField: string) => {
    const hierarchyGroups = {}
    const hierarchyGroupsOrdered: T[][] = []
    if (data.items) {
      data.items.forEach((item) => {
        if (!hierarchyGroups[item[hierarchyField]]) {
          hierarchyGroups[item[hierarchyField]] = []
          hierarchyGroupsOrdered.push(hierarchyGroups[item[hierarchyField]])
        }

        const oldElement = this.dataStore.data.find(
          (oldEl) => oldEl[this.keyField] === item[this.keyField]
        ) // We need to push old element to save shared item link between queries

        if (oldElement) {
          hierarchyGroups[item[hierarchyField]].push(oldElement)
        } else {
          hierarchyGroups[item[hierarchyField]].push(item)
        }
      })
    }
    return hierarchyGroupsOrdered
  }

  private composeKnownRootsItems = (
    groups: T[][],
    expanded: (R | number | null)[],
    hierarchyField: string,
    result: {
      groups: T[][]
      items?: T[]
      total: number
      unknownRoots?: (R | null)[]
    }
  ) => {
    groups.forEach((item: T[]) => {
      try {
        if (item[0]) {
          const index = expanded.indexOf(item[0][hierarchyField])
          if (index > -1) {
            if (!result.items) {
              result.items = []
            }
            expanded[index] = -1
            result.items = [...result.items, ...item]
          }
        }
      } catch (e) {
        result.items = []
      }
    })
    const unknown = expanded.filter((el) => el !== -1)
    result.unknownRoots = unknown as (R | null)[]
  }
}
